import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { Check, Copy } from 'lucide-react';
import { type ChangeEvent, useEffect, useMemo, useState } from 'react';

import { useAccess } from '../../auth/useAccess.js';
import { useFeatureAccess } from '../../auth/useFeatureAccess.js';
import { apiFetch } from '../../lib/apiFetch.js';
import { type AppearanceMode, readAppearance, setAppearance } from '../../lib/appearance.js';
import { BrandColoursCard } from './components/BrandColoursCard.js';
import { CrewStepBackCard } from './components/CrewStepBackCard.js';
import { ServiceTitanCard } from './components/ServiceTitanCard.js';
import { SignInCodeSettingsCard } from './components/SignInCodeSettingsCard.js';
import { StripeConnectCard } from './components/StripeConnectCard.js';
import { WeekCheckSettingsCard } from './components/WeekCheckSettingsCard.js';
import type { SettingsDto, SettingsPatchInput } from './contractsMirror.js';
import {
  useJoinCodeMutation,
  useSettingsMutation,
  useSettingsQuery,
  useUploadLogoMutation,
} from './useSettingsQuery.js';

/**
 * Fetch the company logo bytes THROUGH apiFetch (bearer + X-Tenant-Subdomain) and hand
 * an object URL to the `<img>`. A cross-origin `<img src>` pointed at the api host cannot
 * send those headers and would 401 — the same reason `lib/download.ts` exists (T-22-31-03).
 * A 404 (no logo yet) resolves to null → a neutral empty state, NOT an error. The object
 * URL is revoked on unmount / when `logoPath` changes.
 */
function useLogoPreview(logoPath: string | null): string | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoPath) {
      setObjectUrl(null);
      return;
    }
    let created: string | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch('/api/v1/settings/logo');
        if (!res.ok) {
          if (!cancelled) setObjectUrl(null);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      } catch {
        if (!cancelled) setObjectUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [logoPath]);

  return objectUrl;
}

function LogoField({ settings }: { settings: SettingsDto }) {
  const { t } = useTranslation();
  const uploadLogo = useUploadLogoMutation();
  const previewUrl = useLogoPreview(settings.logoPath);

  function onSelectLogo(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const formData = new FormData();
    formData.set('logo', file);
    uploadLogo.mutate(formData);
  }

  return (
    <div>
      <span className="font-medium text-foreground text-sm">
        {t('settings:branding.logoLabel')}
      </span>
      <div className="mt-2 flex flex-wrap items-center gap-4">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={t('settings:branding.logoAlt')}
            className="h-16 w-auto max-w-[12rem] rounded-md border border-border bg-background object-contain p-1"
          />
        ) : (
          <div className="flex h-16 w-40 items-center justify-center rounded-md border border-border border-dashed bg-background px-2 text-center text-muted-foreground text-xs">
            {t('settings:branding.logoEmpty')}
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          aria-label={t('settings:branding.logoUpload')}
          onChange={onSelectLogo}
          disabled={uploadLogo.isPending}
          className="text-foreground text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:font-medium file:text-secondary-foreground file:text-sm"
        />
      </div>
      {uploadLogo.isPending ? (
        <p className="mt-2 text-muted-foreground text-xs">{t('settings:branding.logoUploading')}</p>
      ) : null}
      {uploadLogo.isError ? (
        <p role="alert" className="mt-2 text-destructive text-sm">
          {t('settings:branding.logoError')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The redesigned Settings screen (UI-SPEC archetype E — form-heavy): grouped section cards
 * (Company · Branding · Crew Onboarding · Preferences · Fleet reminders) with a sticky Save bar at the bottom of
 * the long form. The form reads from the coarse `GET /api/v1/settings` and saves the mutable
 * subset with `PATCH /api/v1/settings` — the SPA never sends a tenant id (the server resolves
 * the principal, SEC-11). Every color is a locked token (BRAND-06).
 *
 * Crew Onboarding surfaces the tenant's crew self-join URL and a copy affordance. Join-code
 * creation/regeneration is handled by `POST /api/v1/settings/join-code`.
 */

const SECTION = 'rounded-md border border-border bg-card p-6';
const SECTION_HEADING = 'mb-1 font-display font-semibold text-foreground text-lg';
const SECTION_DESC = 'mb-4 text-muted-foreground text-sm';
const FIELD_LABEL = 'font-medium text-foreground text-sm';
const INPUT =
  'mt-1 h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const TEXTAREA =
  'mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const FLEET_KEYS = [
  'fleetOilChangeMiles',
  'fleetOilChangeDays',
  'fleetTireRotationMiles',
  'fleetTireRotationDays',
  'fleetInspectionDays',
] as const;
type FleetKey = (typeof FLEET_KEYS)[number];

// The fleet intervals stay strings in form state: Number('') is 0, and a 0 interval means "no
// reminder" on the server, so a cleared field must be left out of the PATCH, not sent as 0.
type FormState = Record<FleetKey, string> &
  Pick<
    SettingsDto,
    | 'name'
    | 'invoicePrefix'
    | 'companyEmail'
    | 'companyPhone'
    | 'companyAddress'
    | 'companyWebsite'
    | 'proposalLicenseInfo'
    | 'proposalDefaultTerms'
    | 'proposalDefaultAcknowledgment'
    | 'notificationCcEmails'
    | 'defaultTaxRate'
    | 'defaultLaborRate'
  >;

function toFormState(s: SettingsDto): FormState {
  return {
    name: s.name,
    invoicePrefix: s.invoicePrefix,
    companyEmail: s.companyEmail,
    companyPhone: s.companyPhone,
    companyAddress: s.companyAddress,
    companyWebsite: s.companyWebsite,
    proposalLicenseInfo: s.proposalLicenseInfo,
    proposalDefaultTerms: s.proposalDefaultTerms,
    proposalDefaultAcknowledgment: s.proposalDefaultAcknowledgment,
    notificationCcEmails: s.notificationCcEmails,
    defaultTaxRate: s.defaultTaxRate,
    defaultLaborRate: s.defaultLaborRate,
    fleetOilChangeMiles: String(s.fleetOilChangeMiles),
    fleetOilChangeDays: String(s.fleetOilChangeDays),
    fleetTireRotationMiles: String(s.fleetTireRotationMiles),
    fleetTireRotationDays: String(s.fleetTireRotationDays),
    fleetInspectionDays: String(s.fleetInspectionDays),
  };
}

/** Whole numbers only; a blank field is omitted so the stored interval is kept. */
function fleetPatch(form: FormState): Partial<Record<FleetKey, number>> {
  return Object.fromEntries(
    FLEET_KEYS.filter((key) => form[key].trim() !== '').map((key) => [
      key,
      Math.round(Number(form[key]) || 0),
    ]),
  );
}

function crewOnboardingLink(joinCode: string | null): string {
  if (typeof window === 'undefined') return '';
  if (!joinCode) return '';
  return `${window.location.origin}/join/${joinCode}`;
}

function SettingsForm({ settings }: { settings: SettingsDto }) {
  const { t } = useTranslation();
  const mutation = useSettingsMutation();
  const joinCodeMutation = useJoinCodeMutation();
  // Mirrors requireApiAdmin on the ServiceTitan routes — a ROLE check, not a permission key, so an
  // override that happens to look integration-shaped cannot surface the card.
  const access = useAccess();
  const canAdmin = access({ minRole: 'Admin' });
  // PATCH /settings, the logo, the join code and Stripe Connect all require settings.manage. Without
  // it the company sections are read-only and there is no Save, rather than a Save that 403s.
  const canManage = access({ permission: 'settings.manage' });
  // ...and the tenant has to HAVE the integration. ServiceTitan belongs to one customer, so for
  // everyone else the card is not "locked", it does not exist — the api worker 404s it.
  const hasServiceTitan = useFeatureAccess()('/servicetitan');

  const [form, setForm] = useState<FormState>(() => toFormState(settings));
  // Re-sync when a refetch lands fresh server state.
  useEffect(() => {
    setForm(toFormState(settings));
  }, [settings]);

  const [copied, setCopied] = useState(false);
  const [appearance, setAppearanceState] = useState<AppearanceMode>(() => readAppearance());
  const hasJoinCode = settings.joinCode !== null && settings.joinCode !== '';
  const onboardingLink = useMemo(() => crewOnboardingLink(settings.joinCode), [settings.joinCode]);
  const linkEnabled = hasJoinCode && settings.joinCodeEnabled;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const input: SettingsPatchInput = {
      name: form.name,
      invoicePrefix: form.invoicePrefix,
      companyEmail: form.companyEmail,
      companyPhone: form.companyPhone,
      companyAddress: form.companyAddress,
      companyWebsite: form.companyWebsite,
      proposalLicenseInfo: form.proposalLicenseInfo,
      proposalDefaultTerms: form.proposalDefaultTerms,
      proposalDefaultAcknowledgment: form.proposalDefaultAcknowledgment,
      notificationCcEmails: form.notificationCcEmails,
      defaultTaxRate: Number(form.defaultTaxRate) || 0,
      defaultLaborRate: Number(form.defaultLaborRate) || 0,
      ...fleetPatch(form),
    };
    mutation.mutate(input);
  }

  async function copyLink() {
    if (!onboardingLink) return;
    try {
      await navigator.clipboard.writeText(onboardingLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  function generateJoinCode() {
    joinCodeMutation.mutate({ enabled: true });
  }

  function updateAppearance(mode: AppearanceMode) {
    setAppearance(mode);
    setAppearanceState(mode);
  }

  return (
    <form className="space-y-4 pb-24" onSubmit={onSubmit}>
      <fieldset disabled={!canManage} className="min-w-0 space-y-4">
        {/* ── Company ─────────────────────────────────────────────────────────── */}
        <section className={SECTION}>
          <h2 className={SECTION_HEADING}>{t('settings:company.heading')}</h2>
          <p className={SECTION_DESC}>{t('settings:company.description')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="s-name" className={FIELD_LABEL}>
                {t('settings:company.name')}
              </label>
              <input
                id="s-name"
                className={INPUT}
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-email" className={FIELD_LABEL}>
                {t('settings:company.email')}
              </label>
              <input
                id="s-email"
                type="email"
                className={INPUT}
                value={form.companyEmail}
                onChange={(e) => update('companyEmail', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-phone" className={FIELD_LABEL}>
                {t('settings:company.phone')}
              </label>
              <input
                id="s-phone"
                className={INPUT}
                value={form.companyPhone}
                onChange={(e) => update('companyPhone', e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="s-address" className={FIELD_LABEL}>
                {t('settings:company.address')}
              </label>
              <input
                id="s-address"
                className={INPUT}
                value={form.companyAddress}
                onChange={(e) => update('companyAddress', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-website" className={FIELD_LABEL}>
                {t('settings:company.website')}
              </label>
              <input
                id="s-website"
                className={INPUT}
                value={form.companyWebsite}
                onChange={(e) => update('companyWebsite', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* ── Branding / Proposals ────────────────────────────────────────────── */}
        <section className={SECTION}>
          <h2 className={SECTION_HEADING}>{t('settings:branding.heading')}</h2>
          <p className={SECTION_DESC}>{t('settings:branding.description')}</p>
          <div className="grid gap-4">
            <LogoField settings={settings} />
            <div>
              <label htmlFor="s-prefix" className={FIELD_LABEL}>
                {t('settings:branding.invoicePrefix')}
              </label>
              <input
                id="s-prefix"
                className={INPUT}
                value={form.invoicePrefix}
                onChange={(e) => update('invoicePrefix', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-license" className={FIELD_LABEL}>
                {t('settings:branding.licenseInfo')}
              </label>
              <textarea
                id="s-license"
                rows={2}
                className={TEXTAREA}
                value={form.proposalLicenseInfo}
                onChange={(e) => update('proposalLicenseInfo', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-terms" className={FIELD_LABEL}>
                {t('settings:branding.defaultTerms')}
              </label>
              <textarea
                id="s-terms"
                rows={3}
                className={TEXTAREA}
                value={form.proposalDefaultTerms}
                onChange={(e) => update('proposalDefaultTerms', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-ack" className={FIELD_LABEL}>
                {t('settings:branding.defaultAcknowledgment')}
              </label>
              <textarea
                id="s-ack"
                rows={3}
                className={TEXTAREA}
                value={form.proposalDefaultAcknowledgment}
                onChange={(e) => update('proposalDefaultAcknowledgment', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* ── Crew app colours (owners edit; others read) ───────────────────── */}
        <BrandColoursCard />

        {/* ── Online Payments (Stripe Connect) ────────────────────────────────── */}
        <StripeConnectCard
          stripeEnabled={settings.stripeEnabled}
          stripeConnected={settings.stripeConnected}
        />
      </fieldset>

      {/* ── ServiceTitan ────────────────────────────────────────────────────
          Admin only, mirroring requireApiAdmin on the routes behind it. These credentials belong
          to the whole tenant, which is a narrower audience than the Manager+ that most money
          surfaces use. Hiding the card is UX — the api worker enforces the same rule again. */}
      {canAdmin && hasServiceTitan ? <ServiceTitanCard /> : null}

      {/* ── Crew Onboarding ─────────────────────────────────────────────────── */}
      <fieldset disabled={!canManage} className="min-w-0">
        <section className={SECTION}>
          <h2 className={SECTION_HEADING}>{t('settings:crew.heading')}</h2>
          <p className={SECTION_DESC}>{t('settings:crew.description')}</p>
          {linkEnabled ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                readOnly
                aria-label={t('settings:crew.linkLabel')}
                className={`${INPUT} mt-0 max-w-md flex-1`}
                value={onboardingLink}
              />
              <Button type="button" variant="secondary" onClick={() => void copyLink()}>
                {copied ? (
                  <>
                    <Check className="size-4" aria-hidden="true" /> {t('settings:crew.copied')}
                  </>
                ) : (
                  <>
                    <Copy className="size-4" aria-hidden="true" /> {t('settings:crew.copy')}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={joinCodeMutation.isPending}
                onClick={generateJoinCode}
              >
                {t('settings:crew.regenerate')}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="secondary"
              disabled={joinCodeMutation.isPending}
              onClick={generateJoinCode}
            >
              {t('settings:crew.generate')}
            </Button>
          )}
          {!settings.joinCodeEnabled && hasJoinCode ? (
            <p className="mt-2 text-muted-foreground text-sm">{t('settings:crew.disabled')}</p>
          ) : null}
          {joinCodeMutation.isError ? (
            <p role="alert" className="mt-2 text-destructive text-sm">
              {t('settings:crew.error')}
            </p>
          ) : null}
        </section>
      </fieldset>

      {/* ── Phone sign-in codes (owners edit; others read) ──────────────────── */}
      <SignInCodeSettingsCard />

      {/* ── Job steps (owners edit; others read) ────────────────────────────── */}
      <CrewStepBackCard />

      {/* ── Appearance ──────────────────────────────────────────────────────── */}
      <section className={SECTION}>
        <h2 className={SECTION_HEADING}>{t('settings:appearance.heading')}</h2>
        <p className={SECTION_DESC}>{t('settings:appearance.description')}</p>
        <label htmlFor="s-appearance" className={FIELD_LABEL}>
          {t('settings:appearance.label')}
        </label>
        <select
          id="s-appearance"
          className={INPUT}
          value={appearance}
          onChange={(event) => updateAppearance(event.target.value as AppearanceMode)}
        >
          <option value="light">{t('settings:appearance.light')}</option>
          <option value="dark">{t('settings:appearance.dark')}</option>
        </select>
        <p className="mt-2 text-muted-foreground text-xs">{t('settings:appearance.note')}</p>
      </section>

      {/* ── Preferences ─────────────────────────────────────────────────────── */}
      <fieldset disabled={!canManage} className="min-w-0 space-y-4">
        <section className={SECTION}>
          <h2 className={SECTION_HEADING}>{t('settings:preferences.heading')}</h2>
          <p className={SECTION_DESC}>{t('settings:preferences.description')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="s-tax" className={FIELD_LABEL}>
                {t('settings:preferences.defaultTaxRate')}
              </label>
              <input
                id="s-tax"
                type="number"
                min="0"
                step="0.01"
                className={INPUT}
                value={form.defaultTaxRate}
                onChange={(e) => update('defaultTaxRate', Number(e.target.value))}
              />
            </div>
            <div>
              <label htmlFor="s-labor" className={FIELD_LABEL}>
                {t('settings:preferences.defaultLaborRate')}
              </label>
              <input
                id="s-labor"
                type="number"
                min="0"
                step="0.01"
                className={INPUT}
                value={form.defaultLaborRate}
                onChange={(e) => update('defaultLaborRate', Number(e.target.value))}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="s-cc" className={FIELD_LABEL}>
                {t('settings:preferences.notificationCcEmails')}
              </label>
              <input
                id="s-cc"
                className={INPUT}
                value={form.notificationCcEmails}
                onChange={(e) => update('notificationCcEmails', e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* ── Fleet reminders ─────────────────────────────────────────────────── */}
        <section className={SECTION}>
          <h2 className={SECTION_HEADING}>{t('settings:fleet.heading')}</h2>
          <p className={SECTION_DESC}>{t('settings:fleet.description')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="s-fleet-oil-miles" className={FIELD_LABEL}>
                {t('settings:fleet.oilChangeMiles')}
              </label>
              <input
                id="s-fleet-oil-miles"
                type="number"
                min="0"
                step="1"
                className={INPUT}
                value={form.fleetOilChangeMiles}
                onChange={(e) => update('fleetOilChangeMiles', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-fleet-oil-days" className={FIELD_LABEL}>
                {t('settings:fleet.oilChangeDays')}
              </label>
              <input
                id="s-fleet-oil-days"
                type="number"
                min="0"
                step="1"
                className={INPUT}
                value={form.fleetOilChangeDays}
                onChange={(e) => update('fleetOilChangeDays', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-fleet-tire-miles" className={FIELD_LABEL}>
                {t('settings:fleet.tireRotationMiles')}
              </label>
              <input
                id="s-fleet-tire-miles"
                type="number"
                min="0"
                step="1"
                className={INPUT}
                value={form.fleetTireRotationMiles}
                onChange={(e) => update('fleetTireRotationMiles', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-fleet-tire-days" className={FIELD_LABEL}>
                {t('settings:fleet.tireRotationDays')}
              </label>
              <input
                id="s-fleet-tire-days"
                type="number"
                min="0"
                step="1"
                className={INPUT}
                value={form.fleetTireRotationDays}
                onChange={(e) => update('fleetTireRotationDays', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="s-fleet-inspection-days" className={FIELD_LABEL}>
                {t('settings:fleet.inspectionDays')}
              </label>
              <input
                id="s-fleet-inspection-days"
                type="number"
                min="0"
                step="1"
                className={INPUT}
                value={form.fleetInspectionDays}
                onChange={(e) => update('fleetInspectionDays', e.target.value)}
              />
            </div>
          </div>
          <p className="mt-4 text-muted-foreground text-sm">{t('settings:fleet.zeroHint')}</p>
        </section>
      </fieldset>

      {/* ── Weekly hours check (owners edit; others read) ───────────────────── */}
      <WeekCheckSettingsCard />

      {/* ── Sticky Save bar ─────────────────────────────────────────────────── */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-border border-t bg-card/95 px-6 py-3 backdrop-blur md:left-60">
        <div className="flex flex-wrap items-center justify-end gap-3">
          {mutation.isError ? (
            <p role="alert" className="mr-auto text-destructive text-sm">
              {t('settings:save.error')}
            </p>
          ) : null}
          {mutation.isSuccess ? (
            <p role="status" className="mr-auto text-secondary-foreground text-sm">
              {t('settings:save.success')}
            </p>
          ) : null}
          {canManage ? (
            <Button type="submit" disabled={mutation.isPending}>
              {t('settings:save.cta')}
            </Button>
          ) : (
            <p className="text-muted-foreground text-sm">{t('settings:save.readOnly')}</p>
          )}
        </div>
      </div>
    </form>
  );
}

export function SettingsRoute() {
  const { t } = useTranslation();
  const query = useSettingsQuery();

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError || !query.data?.settings) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('settings:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('settings:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('settings:error.retry')}
        </button>
      </section>
    );
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {t('settings:title')}
        </h1>
        <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('settings:subtitle')}</p>
      </header>
      <SettingsForm settings={query.data.settings} />
    </div>
  );
}
